import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Edit2, ChevronRight, FolderOpen, Package, Leaf, ArrowLeft, Trash2, Download } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";
import { useAuth } from "@/contexts/AuthContext";

export default function CataloguePage() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdminRole = (user?.roles || []).some(r => ["Admin", "admin"].includes(r));
  const has = (...keys) => isAdminRole || keys.some(k => perms.includes(k));
  const canCreateCategory = isAdminRole;

  const downloadCsv = async (url, fallbackName) => {
    try {
      const { data } = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data])); a.download = fallbackName; a.click();
    } catch { toast.error("Export failed"); }
  };
  const canCreateProduct = has("catalogue.create_product", "admin.full_access");
  const canCreateSKU = has("catalogue.create_sku", "admin.full_access");
  const canSetPricing = has("catalogue.set_pricing", "admin.full_access");
  const canSetDiscount = has("catalogue.set_discount_rules", "admin.full_access");
  const canViewCogs = has("catalogue.view_cogs", "admin.full_access");
  const canEdit = canCreateCategory || canCreateProduct || canCreateSKU;
  const { getValues, getItems } = useConfig(ALL_CONFIG_GROUPS);
  const PACK_FORMATS = getValues("pack_formats");
  const WEIGHT_UNITS = getValues("weight_units");
  const GST_RATES = getItems("gst_rates").map(i => parseInt(i.metadata?.rate ?? i.key));
  const FOOD_TYPES = getValues("food_types");
  const PRIMARY_PACKAGING = getValues("primary_packaging");
  const SECONDARY_PACKAGING = getValues("secondary_packaging");
  const OUTER_PACKAGING = getValues("outer_packaging");
  const GST_TREATMENTS = getValues("gst_treatments");
  const SKU_STATUSES = getValues("sku_statuses");
  const STATUSES = SKU_STATUSES;

  // Navigation state
  const [level, setLevel] = useState("categories");
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedProd, setSelectedProd] = useState(null);
  const [editingSKU, setEditingSKU] = useState(null);
  const [viewMode, setViewMode] = useState("sku"); // "sku" | "channel"
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelSkuEdits, setChannelSkuEdits] = useState({}); // {sku_id: {dirty, selling_price, max_discount_percent, moq, enabled}}

  // Data
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [skus, setSkus] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Advanced SKU filters
  const [skuIdFilter, setSkuIdFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [mrpFrom, setMrpFrom] = useState("");
  const [mrpTo, setMrpTo] = useState("");
  const [channelFilter, setChannelFilter] = useState("");

  // Dialogs
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showSKUDialog, setShowSKUDialog] = useState(false);

  // Product form
  const [prodForm, setProdForm] = useState({ name: "", brand: "SKIPPI", brand_id: "", description: "", food_type: "Vegetarian", allergen_info: "", storage_condition: "", shelf_life_days: 0, fssai_licence: "", status: "Active" });
  // Prompt 2.1 C5 — brand dropdown when the Brands module is on
  const brandsFlagOn = (getItems("feature_flags") || []).some(i => i.key === "BRANDS" && i.value === "true");
  const [brandOptions, setBrandOptions] = useState([]);
  useEffect(() => {
    if (!brandsFlagOn) return;
    api.get("/brands/options?live_only=true").then(r => setBrandOptions(r.data || [])).catch(() => {});
  }, [brandsFlagOn]);

  // SKU form
  const [skuForm, setSkuForm] = useState({
    sku_name: "", variant_name: "", status: "Active", pack_format: "Single Unit",
    units_per_pack: 1, net_weight_volume: "", weight_unit: "ml", gross_weight: "",
    barcode: "", primary_packaging: "", secondary_packaging: "", outer_carton: "",
    units_per_inner_box: 0, units_per_master_carton: 0, carton_weight: "",
    mrp: 0, base_cost: 0, hsn_code: "", gst_percent: 18, gst_treatment: "Exclusive",
    reorder_level: 0, moq: 1, channel_access: [],
  });
  const [skuDraftBanner, setSkuDraftBanner] = useState(false);
  const [skuDraftKey, setSkuDraftKey] = useState("");

  const fmt = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(n || 0);

  // Channel View: save individual SKU channel_access update
  const saveChannelSKU = async (sku, channelId) => {
    const edit = channelSkuEdits[sku.sku_id];
    if (!edit) return;
    if (edit.enabled && (!edit.selling_price || edit.selling_price <= 0)) {
      toast.error(`Selling price is required to enable ${sku.sku_name} on this Sales Channel`);
      return;
    }
    const updatedCA = (sku.channel_access || []).map(ca => {
      if (ca.channel_id === channelId) {
        return { ...ca, enabled: edit.enabled, selling_price: edit.selling_price, max_discount_percent: edit.max_discount_percent, moq: edit.moq };
      }
      return ca;
    });
    // If no existing entry for this channel, add one
    if (!updatedCA.find(ca => ca.channel_id === channelId)) {
      updatedCA.push({ channel_id: channelId, channel_name: selectedChannel?.name || "", enabled: edit.enabled, selling_price: edit.selling_price, max_discount_percent: edit.max_discount_percent || 0, moq: edit.moq || 0 });
    }
    try {
      await api.put(`/catalogue/skus/${sku.sku_id}`, { channel_access: updatedCA });
      toast.success(`Sales Channel pricing updated for ${sku.sku_name}`);
      setChannelSkuEdits(prev => { const n = {...prev}; delete n[sku.sku_id]; return n; });
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Auto-save SKU draft to localStorage
  useEffect(() => {
    if (!showSKUDialog || editingSKU || !skuDraftKey) return;
    const timer = setInterval(() => {
      if (skuForm.sku_name) sessionStorage.setItem(skuDraftKey, JSON.stringify(skuForm));
    }, 3000);
    return () => clearInterval(timer);
  }, [showSKUDialog, skuForm, skuDraftKey, editingSKU]);

  // Load data based on current level
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (level === "categories") {
        const { data } = await api.get("/admin/categories");
        setCategories(data.filter(c => c.status === "Active"));
      } else if (level === "products") {
        const { data } = await api.get("/catalogue/products", { params: { category_id: selectedCat?.category_id, search: search || undefined } });
        setProducts(data);
      } else if (level === "skus") {
        const [skuRes, chRes] = await Promise.all([
          api.get("/catalogue/skus", { params: { product_id: selectedProd?.product_id, search: search || undefined } }),
          api.get("/channels"),
        ]);
        setSkus(skuRes.data);
        setChannels(chRes.data);
      }
      // Always load channels for Sales Channel View
      if (channels.length === 0) {
        api.get("/channels").then(r => setChannels(r.data)).catch(err => console.error("API get /channels error:", err));
      }
      // Always load all SKUs for Sales Channel View
      if (viewMode === "channel" && skus.length === 0) {
        api.get("/catalogue/skus").then(r => setSkus(r.data)).catch(err => console.error("API get /catalogue/skus error:", err));
      }
    } catch (err) { console.error("API get /catalogue/skus error:", err); } finally { setLoading(false); }
  }, [level, selectedCat, selectedProd, search, viewMode]);

  useEffect(() => { loadData(); }, [loadData]);

  // Navigation
  const goToCategories = () => { setLevel("categories"); setSelectedCat(null); setSelectedProd(null); setSearch(""); };
  const goToProducts = (cat) => { setSelectedCat(cat); setLevel("products"); setSelectedProd(null); setSearch(""); };
  const goToSKUs = (prod) => { setSelectedProd(prod); setLevel("skus"); setSearch(""); };

  // Product CRUD
  const openCreateProduct = () => { setEditingProduct(null); setProdForm({ name: "", brand: "SKIPPI", brand_id: "", description: "", food_type: "Vegetarian", allergen_info: "", storage_condition: "", shelf_life_days: 0, fssai_licence: "", status: "Active" }); setShowProductDialog(true); };
  const openEditProduct = (p) => { setEditingProduct(p); setProdForm({ name: p.name, brand: p.brand || "SKIPPI", brand_id: p.brand_id || "", description: p.description || "", food_type: p.food_type || "Vegetarian", allergen_info: p.allergen_info || "", storage_condition: p.storage_condition || "", shelf_life_days: p.shelf_life_days || 0, fssai_licence: p.fssai_licence || "", status: p.status || "Active" }); setShowProductDialog(true); };

  const saveProduct = async () => {
    try {
      if (editingProduct) {
        await api.put(`/catalogue/products/${editingProduct.product_id}`, prodForm);
        toast.success("Product updated");
      } else {
        await api.post("/catalogue/products", { ...prodForm, category_id: selectedCat.category_id });
        toast.success("Product created");
      }
      setShowProductDialog(false); loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // SKU CRUD
  const openCreateSKU = async () => {
    setEditingSKU(null);
    const prodId = selectedProd?.product_id || "new";
    const draftKey = `orbit_sku_draft_${prodId}`;
    setSkuDraftKey(draftKey);
    const saved = sessionStorage.getItem(draftKey);
    if (saved) {
      setSkuDraftBanner(true);
    } else {
      setSkuDraftBanner(false);
    }
    const { data: chs } = await api.get("/channels");
    setChannels(chs);
    setSkuForm({
      sku_id: "", sku_name: "", variant_name: "", status: "Active", pack_format: "Single Unit",
      units_per_pack: 1, net_weight_volume: "", weight_unit: "ml", gross_weight: "",
      barcode: "", primary_packaging: "", secondary_packaging: "", outer_carton: "",
      units_per_inner_box: 0, units_per_master_carton: 0, carton_weight: "",
      mrp: 0, base_cost: 0, hsn_code: "", gst_percent: 18, gst_treatment: "Exclusive",
      reorder_level: 0, moq: 1,
      channel_access: chs.map(ch => ({ channel_id: ch._id, channel_name: ch.name, enabled: false, selling_price: 0, moq: 0, max_discount_percent: 0 })),
    });
    setShowSKUDialog(true);
  };

  const openEditSKU = async (sku) => {
    setEditingSKU(sku);
    const { data: chs } = await api.get("/channels");
    setChannels(chs);
    const existingCA = sku.channel_access || [];
    // Build channel_access from fresh channel list — always use current channel name
    const ca = chs.map(ch => {
      const existing = existingCA.find(e => e.channel_id === ch._id);
      return existing
        ? { ...existing, channel_name: ch.name }  // Update name from fresh channel data
        : { channel_id: ch._id, channel_name: ch.name, enabled: false, selling_price: 0, moq: 0, max_discount_percent: 0, carton_multiple_override: null };
    });
    setSkuForm({
      sku_id: sku.sku_id || "", sku_name: sku.sku_name || "", variant_name: sku.variant_name || "", status: sku.status || "Active",
      pack_format: sku.pack_format || "Single Unit", units_per_pack: sku.units_per_pack || 1,
      net_weight_volume: sku.net_weight_volume || "", weight_unit: sku.weight_unit || "ml",
      gross_weight: sku.gross_weight || "", barcode: sku.barcode || "",
      primary_packaging: sku.primary_packaging || "", secondary_packaging: sku.secondary_packaging || "",
      outer_carton: sku.outer_carton || "", units_per_inner_box: sku.units_per_inner_box || 0,
      units_per_master_carton: sku.units_per_master_carton || 0, carton_weight: sku.carton_weight || "",
      mrp: sku.mrp || 0, base_cost: sku.base_cost || 0, hsn_code: sku.hsn_code || "",
      gst_percent: sku.gst_percent ?? 18, gst_treatment: sku.gst_treatment || "Exclusive",
      reorder_level: sku.reorder_level || 0, moq: sku.moq || 1, channel_access: ca,
    });
    setShowSKUDialog(true);
  };

  const updateChannelAccess = (idx, field, value) => {
    const ca = [...skuForm.channel_access];
    ca[idx] = { ...ca[idx], [field]: value };
    setSkuForm({ ...skuForm, channel_access: ca });
  };

  const saveSKU = async (forceStatus) => {
    if (forceStatus !== "Draft") {
      // Full validation for non-Draft saves
      for (const ca of skuForm.channel_access) {
        if (ca.enabled && (!ca.selling_price || ca.selling_price <= 0)) {
          toast.error(`Please enter a selling price for ${ca.channel_name}`);
          return;
        }
      }
    }
    if (!skuForm.sku_name && forceStatus !== "Draft") {
      toast.error("SKU name is required"); return;
    }
    if (editingSKU && !skuForm.sku_id) {
      toast.error("SKU Code cannot be empty"); return;
    }
    if (editingSKU && skuForm.sku_id && skuForm.sku_id !== editingSKU.sku_id) {
      if (!window.confirm("Changing the SKU code will affect historical orders. Are you sure?")) return;
    }
    const payload = forceStatus ? { ...skuForm, status: forceStatus } : skuForm;
    try {
      if (editingSKU) {
        await api.put(`/catalogue/skus/${editingSKU.sku_id}`, payload);
        toast.success(forceStatus === "Draft" ? "SKU saved as Draft" : "SKU updated — pending approval if active");
      } else {
        if (!skuForm.sku_name) { toast.error("SKU name is required"); return; }
        await api.post("/catalogue/skus", { ...payload, product_id: selectedProd.product_id });
        toast.success("SKU created — pending approval");
      }
      if (skuDraftKey) sessionStorage.removeItem(skuDraftKey);
      setShowSKUDialog(false); loadData();
    } catch (err) {
      if (err.response?.status === 409) {
        const d = err.response.data?.detail || err.response.data;
        const msg = typeof d === "object" ? d.message : d;
        const clash = typeof d === "object" ? d.clashing_sku_id : "";
        if (window.confirm(`${msg}${clash ? ` (${clash})` : ""}\n\nContinue anyway?`)) {
          try {
            await api.post("/catalogue/skus", { ...payload, product_id: selectedProd.product_id, confirm_duplicate: true });
            toast.success("SKU created — pending approval");
            if (skuDraftKey) sessionStorage.removeItem(skuDraftKey);
            setShowSKUDialog(false); loadData();
          } catch (err2) { toast.error(formatApiError(err2.response?.data?.detail)); }
        }
      } else { toast.error(formatApiError(err.response?.data?.detail)); }
    }
  };

  const requestSKUDeletion = async (sku) => {
    const reason = window.prompt(`Request deletion of ${sku.sku_name}?\n\nEnter a reason (mandatory):`);
    if (!reason) return;
    try {
      await api.put(`/catalogue/skus/${sku.sku_id}`, { status: "Pending Deletion", deletion_requested: true, deletion_reason: reason, deletion_requested_by: user?.name || "" });
      toast.success("SKU deletion requested — pending Admin approval");
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const approveSKUDeletion = async (sku, approve) => {
    try {
      if (approve) {
        await api.put(`/catalogue/skus/${sku.sku_id}`, { status: "Discontinued", is_deleted: true, deletion_requested: false });
        toast.success(`${sku.sku_name} deleted (soft delete — historical data preserved)`);
      } else {
        const reason = window.prompt("Rejection reason:");
        await api.put(`/catalogue/skus/${sku.sku_id}`, { status: "Active", deletion_requested: false, deletion_reason: "" });
        toast.success(`Deletion of ${sku.sku_name} rejected`);
      }
      loadData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const statusCls = (s) => s === "Active" ? "status-active" : s === "Discontinued" ? "status-inactive" : s === "Seasonal" || s === "Coming Soon" ? "status-warning" : s === "Blocked" ? "status-danger" : "status-inactive";

  // ─── BREADCRUMB ─────────────────────────────────────────────
  const Breadcrumb = () => (
    <div className="flex items-center gap-1.5 text-xs mb-6" data-testid="catalogue-breadcrumb">
      <button onClick={goToCategories} className={`hover:underline ${level === "categories" ? "font-semibold text-zinc-900" : "text-zinc-500"}`}>Catalogue</button>
      {selectedCat && <>
        <ChevronRight size={12} className="text-zinc-300" />
        <button onClick={() => goToProducts(selectedCat)} className={`hover:underline ${level === "products" ? "font-semibold text-zinc-900" : "text-zinc-500"}`}>{selectedCat.name}</button>
      </>}
      {selectedProd && <>
        <ChevronRight size={12} className="text-zinc-300" />
        <span className="font-semibold text-zinc-900">{selectedProd.name}</span>
      </>}
    </div>
  );


  // ─── VIEW MODE TOGGLE ─────────────────────────────────────
  const ViewToggle = () => (
    <div className="flex gap-1 mb-4" data-testid="view-toggle">
      <Button variant={viewMode === "sku" ? "default" : "outline"} className="rounded-sm text-xs h-8" onClick={() => setViewMode("sku")}>SKU View</Button>
      <Button variant={viewMode === "channel" ? "default" : "outline"} className="rounded-sm text-xs h-8" onClick={() => { setViewMode("channel"); setSelectedChannel(null); setChannelSkuEdits({}); }}>Sales Channel View</Button>
    </div>
  );

  // ─── SALES CHANNEL VIEW ───────────────────────────────────
  if (viewMode === "channel") {
    const allSkus = skus.length > 0 ? skus : [];
    const channelSkuCounts = channels.reduce((acc, ch) => {
      acc[ch._id] = allSkus.filter(s => (s.channel_access || []).some(ca => ca.channel_id === ch._id && ca.enabled)).length;
      return acc;
    }, {});

    const channelSKUs = selectedChannel
      ? allSkus.map(s => {
          const ca = (s.channel_access || []).find(c => c.channel_id === selectedChannel._id);
          return { ...s, ch_enabled: ca?.enabled || false, ch_selling_price: ca?.selling_price || 0, ch_max_discount: ca?.max_discount_percent || 0, ch_moq: ca?.moq || 0, category_name: categories.find(c => c.category_id === (products.find(p => p.product_id === s.product_id)?.category_id))?.name || "" };
        }).sort((a, b) => a.category_name.localeCompare(b.category_name) || a.sku_name.localeCompare(b.sku_name))
      : [];
    const activeCount = selectedChannel ? channelSKUs.filter(s => { const edit = channelSkuEdits[s.sku_id]; return edit ? edit.enabled : s.ch_enabled; }).length : 0;

    return (
      <div data-testid="catalogue-page">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Product Catalogue</h1>
          <p className="text-sm text-zinc-500 mt-1">Sales Channel View — manage SKU availability and pricing per Sales Channel</p>
        </div>
        <ViewToggle />

        <div className="flex gap-4" style={{minHeight: 400}}>
          {/* Left panel — Sales Channels list */}
          <div className="w-56 shrink-0 border border-zinc-200 rounded-sm overflow-hidden" data-testid="channel-list-panel">
            <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200"><p className="text-[10px] font-semibold uppercase text-zinc-400">Sales Channels</p></div>
            <div className="divide-y divide-zinc-50">
              {channels.map(ch => (
                <button key={ch._id} onClick={() => { setSelectedChannel(ch); setChannelSkuEdits({}); }} className={`w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 flex items-center justify-between ${selectedChannel?._id === ch._id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`} data-testid={`channel-item-${ch._id}`}>
                  <span className="font-medium truncate">{ch.name}</span>
                  <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-sm ${(channelSkuCounts[ch._id] || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{channelSkuCounts[ch._id] || 0} active</span>
                </button>
              ))}
            </div>
          </div>

          {/* Right panel — SKUs for selected channel */}
          <div className="flex-1 border border-zinc-200 rounded-sm overflow-hidden">
            {!selectedChannel ? (
              <div className="flex items-center justify-center h-full text-zinc-400 text-sm py-20">Select a Sales Channel from the left to view SKU assignments</div>
            ) : (
              <>
                <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <div><p className="text-sm font-semibold text-zinc-900">SKUs active on {selectedChannel.name}</p><p className="text-[10px] text-zinc-500">{activeCount} of {channelSKUs.length} SKUs active</p></div>
                  <Button variant="outline" className="h-7 text-xs" onClick={() => { const p = new URLSearchParams(); if (selectedChannel) p.set("channel_id", selectedChannel._id); downloadCsv(`/catalogue/skus/export-channels?${p.toString()}`, "sku_channels_export.csv"); }} data-testid="export-channels-csv"><Download className="w-3 h-3 mr-1" />Export CSV</Button>
                </div>
                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                  <table className="data-table w-full">
                    <thead className="sticky top-0 bg-white z-10"><tr><th>SKU Name</th><th className="font-mono w-24">SKU ID</th><th className="num w-28">Selling Price</th><th className="num w-24">Max Disc %</th><th className="num w-20">MOQ</th><th className="w-16">Enabled</th>{canCreateSKU && <th className="w-16"></th>}</tr></thead>
                    <tbody>
                      {channelSKUs.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs">No SKUs found</td></tr> :
                      (() => {
                        let lastCat = "";
                        return channelSKUs.map((s, idx) => {
                          const edit = channelSkuEdits[s.sku_id];
                          const isEnabled = edit ? edit.enabled : s.ch_enabled;
                          const sp = edit?.selling_price ?? s.ch_selling_price;
                          const md = edit?.max_discount_percent ?? s.ch_max_discount;
                          const moq = edit?.moq ?? s.ch_moq;
                          const isDirty = !!edit?.dirty;
                          const priceError = isEnabled && (!sp || sp <= 0);
                          const showCatHeader = s.category_name !== lastCat;
                          lastCat = s.category_name;
                          return (
                            <React.Fragment key={s.sku_id}>
                              {showCatHeader && <tr className="bg-zinc-50"><td colSpan={7} className="text-[10px] font-semibold uppercase text-zinc-400 py-1">{s.category_name || "Uncategorized"}</td></tr>}
                              <tr data-testid={`channel-sku-${s.sku_id}`}>
                                <td><span className="text-xs font-medium">{s.sku_name}</span><br/><span className="text-[10px] text-zinc-400">{products.find(p => p.product_id === s.product_id)?.name || ""}</span></td>
                                <td className="font-mono text-[10px] text-zinc-500">{s.sku_id}</td>
                                <td>{canCreateSKU ? <Input type="number" step="0.01" value={sp || ""} onChange={e => setChannelSkuEdits(prev => ({...prev, [s.sku_id]: {...(prev[s.sku_id] || {enabled: isEnabled, selling_price: sp, max_discount_percent: md, moq}), selling_price: parseFloat(e.target.value) || 0, dirty: true}}))} className={`h-7 w-24 text-xs rounded-sm text-right ${priceError ? 'border-red-500 bg-red-50' : ''}`} /> : <span className="font-mono text-xs">₹{fmt(sp)}</span>}</td>
                                <td>{canCreateSKU ? <Input type="number" step="0.1" value={md || ""} onChange={e => setChannelSkuEdits(prev => ({...prev, [s.sku_id]: {...(prev[s.sku_id] || {enabled: isEnabled, selling_price: sp, max_discount_percent: md, moq}), max_discount_percent: parseFloat(e.target.value) || 0, dirty: true}}))} className="h-7 w-20 text-xs rounded-sm text-right" /> : <span className="font-mono text-xs">{md}%</span>}</td>
                                <td>{canCreateSKU ? <Input type="number" value={moq || ""} onChange={e => setChannelSkuEdits(prev => ({...prev, [s.sku_id]: {...(prev[s.sku_id] || {enabled: isEnabled, selling_price: sp, max_discount_percent: md, moq}), moq: parseInt(e.target.value) || 0, dirty: true}}))} className="h-7 w-16 text-xs rounded-sm text-right" /> : <span className="font-mono text-xs">{moq}</span>}</td>
                                <td><Switch checked={isEnabled} disabled={!canCreateSKU} onCheckedChange={v => setChannelSkuEdits(prev => ({...prev, [s.sku_id]: {...(prev[s.sku_id] || {selling_price: sp, max_discount_percent: md, moq}), enabled: v, dirty: true}}))} className="scale-75" /></td>
                                {canCreateSKU && <td>{isDirty && <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => saveChannelSKU(s, selectedChannel._id)} data-testid={`save-channel-sku-${s.sku_id}`}>Save</Button>}</td>}
                              </tr>
                              {priceError && <tr><td colSpan={7} className="text-[9px] text-red-600 px-3 py-0.5">Selling price is required to enable this Sales Channel</td></tr>}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }


  // ─── CATEGORIES VIEW ────────────────────────────────────────
  if (level === "categories") return (
    <div data-testid="catalogue-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Product Catalogue</h1>
        <p className="text-sm text-zinc-500 mt-1">Browse by category. Manage categories in Admin Setup.</p>
      </div>
      <ViewToggle />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="catalogue-categories">
        {loading ? <p className="text-xs text-zinc-400 col-span-4 text-center py-12">Loading...</p> :
        categories.length === 0 ? (
          <div className="col-span-4 text-center py-12">
            <p className="text-sm text-zinc-400 mb-2">No categories found.</p>
            <p className="text-xs text-zinc-400">Go to <span className="font-medium">Admin Setup &gt; Categories</span> to create categories first.</p>
          </div>
        ) :
        categories.map(c => (
          <button key={c.category_id} onClick={() => goToProducts(c)} className="border border-zinc-200 p-5 text-left hover:border-zinc-400 transition-colors group" data-testid={`cat-card-${c.category_id}`}>
            <div className="w-10 h-10 bg-zinc-100 flex items-center justify-center rounded-sm mb-3 group-hover:bg-zinc-200 transition-colors">
              <FolderOpen size={18} className="text-zinc-500" />
            </div>
            <h3 className="font-heading font-semibold text-zinc-900">{c.name}</h3>
            <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{c.category_id}</p>
            <p className="text-xs text-zinc-500 mt-2">{c.product_count || 0} products</p>
          </button>
        ))}
      </div>
    </div>
  );

  // ─── PRODUCTS VIEW ──────────────────────────────────────────
  if (level === "products") return (
    <div data-testid="catalogue-page">
      <Breadcrumb />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={goToCategories} className="p-1 hover:bg-zinc-100 rounded-sm"><ArrowLeft size={16} className="text-zinc-500" /></button>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">{selectedCat.name}</h1>
            <p className="text-xs text-zinc-500">Products in this category</p>
          </div>
        </div>
        {canCreateProduct && <Button onClick={openCreateProduct} className="rounded-sm h-8 text-xs" data-testid="create-product-btn"><Plus size={14} /> Add Product</Button>}
      </div>
      <div className="mb-4">
        <div className="relative w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="products-search" />
        </div>
      </div>
      <div className="border border-zinc-200 overflow-hidden">
        <table className="data-table w-full" data-testid="products-table">
          <thead><tr><th>Product ID</th><th>Name</th><th>Brand</th><th>Food Type</th><th className="num">Shelf Life</th><th className="num">SKUs</th><th>Status</th><th className="w-20">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            products.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-zinc-400 text-xs">No products in this category yet</td></tr> :
            products.map(p => (
              <tr key={p.product_id} data-testid={`product-row-${p.product_id}`}>
                <td className="font-mono text-xs text-zinc-500">{p.product_id}</td>
                <td className="font-medium text-zinc-900">{p.name}</td>
                <td>{p.brand_id ? (brandOptions.find(b => b._id === p.brand_id)?.display_name || p.brand) : p.brand}</td>
                <td><span className="flex items-center gap-1"><Leaf size={10} className={p.food_type === "Vegetarian" ? "text-green-600" : p.food_type === "Vegan" ? "text-green-700" : "text-red-600"} /><span className="text-xs">{p.food_type}</span></span></td>
                <td className="num font-mono">{p.shelf_life_days ? `${p.shelf_life_days} mo` : "-"}</td>
                <td className="num font-mono">{p.sku_count || 0}</td>
                <td><span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${statusCls(p.status)}`}>{p.status}</span></td>
                <td>
                  <div className="flex gap-1">
                    {canCreateProduct && <button onClick={(e) => { e.stopPropagation(); openEditProduct(p); }} className="p-1 hover:bg-zinc-100 rounded-sm"><Edit2 size={13} className="text-zinc-500" /></button>}
                    {has("admin.full_access") && <button onClick={async (e) => { e.stopPropagation(); if (!window.confirm(`Delete product "${p.name}"?`)) return; try { await api.delete(`/catalogue/products/${p.product_id}`); toast.success("Product deleted"); loadData(); } catch { toast.error("Delete failed"); } }} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 size={13} className="text-red-400" /></button>}
                    <button onClick={() => goToSKUs(p)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`view-skus-${p.product_id}`}><ChevronRight size={13} className="text-zinc-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingProduct ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Product Name</Label><Input value={prodForm.name} onChange={(e) => setProdForm({...prodForm, name: e.target.value})} className="mt-1 rounded-sm" data-testid="product-name-input" /></div>
              {brandsFlagOn ? (
                <div>
                  <Label className="text-xs">Brand <span className="text-red-500">*</span></Label>
                  <select className="mt-1 w-full h-9 text-sm border rounded-sm px-2" value={prodForm.brand_id || ""} onChange={(e) => setProdForm({...prodForm, brand_id: e.target.value})} data-testid="product-brand-select">
                    <option value="">Choose a brand</option>
                    {brandOptions.map(b => <option key={b._id} value={b._id}>{b.display_name}</option>)}
                    {editingProduct?.brand_id && !brandOptions.some(b => b._id === editingProduct.brand_id) && (
                      <option value={editingProduct.brand_id}>{editingProduct.brand} (not live)</option>
                    )}
                  </select>
                </div>
              ) : (
                <div><Label className="text-xs">Brand</Label><Input value={prodForm.brand} onChange={(e) => setProdForm({...prodForm, brand: e.target.value})} className="mt-1 rounded-sm" /></div>
              )}
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={prodForm.description} onChange={(e) => setProdForm({...prodForm, description: e.target.value})} className="mt-1 rounded-sm" rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Food Type</Label>
                <Select value={prodForm.food_type} onValueChange={(v) => setProdForm({...prodForm, food_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{FOOD_TYPES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Shelf Life (months)</Label><Input type="number" value={prodForm.shelf_life_days} onChange={(e) => setProdForm({...prodForm, shelf_life_days: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={prodForm.status} onValueChange={(v) => setProdForm({...prodForm, status: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Storage Condition</Label><Input value={prodForm.storage_condition} onChange={(e) => setProdForm({...prodForm, storage_condition: e.target.value})} placeholder="e.g. Store frozen at -18C" className="mt-1 rounded-sm" /></div>
              <div><Label className="text-xs">FSSAI Licence</Label><Input value={prodForm.fssai_licence} onChange={(e) => setProdForm({...prodForm, fssai_licence: e.target.value})} className="mt-1 rounded-sm" /></div>
            </div>
            <div><Label className="text-xs">Allergen Info</Label><Input value={prodForm.allergen_info} onChange={(e) => setProdForm({...prodForm, allergen_info: e.target.value})} className="mt-1 rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={saveProduct} className="rounded-sm text-xs" data-testid="save-product-btn">Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // ─── SKUs VIEW ──────────────────────────────────────────────
  return (
    <div data-testid="catalogue-page">
      <Breadcrumb />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => goToProducts(selectedCat)} className="p-1 hover:bg-zinc-100 rounded-sm"><ArrowLeft size={16} className="text-zinc-500" /></button>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">{selectedProd.name}</h1>
            <p className="text-xs text-zinc-500">{selectedCat.name} &middot; SKUs in this product</p>
          </div>
        </div>
        {canCreateSKU && <Button onClick={openCreateSKU} className="rounded-sm h-8 text-xs" data-testid="create-sku-btn"><Plus size={14} /> Add SKU</Button>}
      </div>
      {/* Pending Deletion Banner */}
      {has("admin.full_access") && skus.some(s => s.deletion_requested) && (
        <div className="mb-3 border border-amber-300 bg-amber-50 p-3 rounded-sm" data-testid="pending-deletion-banner">
          <p className="text-xs font-semibold text-amber-800 mb-2">{skus.filter(s => s.deletion_requested).length} SKU(s) pending deletion approval</p>
          {skus.filter(s => s.deletion_requested).map(s => (
            <div key={s.sku_id} className="flex items-center justify-between py-1 border-b border-amber-200 last:border-0">
              <div><span className="text-xs font-medium">{s.sku_name}</span> <span className="text-[10px] text-zinc-500 ml-2">Reason: {s.deletion_reason}</span></div>
              <div className="flex gap-1">
                <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => approveSKUDeletion(s, true)}>Approve</Button>
                <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2" onClick={() => approveSKUDeletion(s, false)}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mb-4 space-y-2">
        <div className="flex gap-2 flex-wrap items-end">
          <div className="relative w-48"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><Input placeholder="Search SKU name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-8 text-xs rounded-sm" data-testid="skus-search" /></div>
          <div><Input placeholder="SKU ID..." value={skuIdFilter} onChange={e => setSkuIdFilter(e.target.value)} className="h-8 text-xs rounded-sm w-28 font-mono" /></div>
          <div><Select value={statusFilter || "all"} onValueChange={v => setStatusFilter(v === "all" ? "" : v)}><SelectTrigger className="h-8 text-xs rounded-sm w-28"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{SKU_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div className="flex items-center gap-1"><Input placeholder="Price ₹ from" type="number" value={priceFrom} onChange={e => setPriceFrom(e.target.value)} className="h-8 text-xs rounded-sm w-24" /><span className="text-zinc-400 text-xs">–</span><Input placeholder="to" type="number" value={priceTo} onChange={e => setPriceTo(e.target.value)} className="h-8 text-xs rounded-sm w-24" /></div>
          <div><Select value={channelFilter || "all"} onValueChange={v => setChannelFilter(v === "all" ? "" : v)}><SelectTrigger className="h-8 text-xs rounded-sm w-36"><SelectValue placeholder="Sales Channel" /></SelectTrigger><SelectContent><SelectItem value="all">All Channels</SelectItem>{channels.map(ch => <SelectItem key={ch._id} value={ch._id}>{ch.name}</SelectItem>)}</SelectContent></Select></div>
          {(skuIdFilter || statusFilter || priceFrom || priceTo || channelFilter) && <Button variant="ghost" className="h-8 text-[10px] text-blue-600" onClick={() => { setSkuIdFilter(""); setStatusFilter(""); setPriceFrom(""); setPriceTo(""); setMrpFrom(""); setMrpTo(""); setChannelFilter(""); }}>Clear Filters</Button>}
          <Button variant="outline" className="h-8 text-xs ml-auto" onClick={() => { const p = new URLSearchParams(); if (selectedProd) p.set("product_id", selectedProd.product_id); if (search) p.set("search", search); if (statusFilter) p.set("status", statusFilter); if (channelFilter) p.set("channel_id", channelFilter); downloadCsv(`/catalogue/skus/export?${p.toString()}`, "skus_export.csv"); }} data-testid="export-skus-csv"><Download className="w-3 h-3 mr-1" />Export CSV</Button>
        </div>
      </div>
      <div className="border border-zinc-200 overflow-x-auto">
        <table className="data-table w-full" data-testid="skus-table">
          <thead><tr><th>SKU ID</th><th style={{minWidth: 200}}>Name</th><th>Variant</th><th>Pack</th><th className="num">Units</th><th className="num">MRP</th><th className="num">GST%</th><th className="num">Channels</th><th>Status</th><th className="w-16">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr> :
            (() => {
              let filtered = skus;
              if (skuIdFilter) filtered = filtered.filter(s => s.sku_id.toLowerCase().includes(skuIdFilter.toLowerCase()));
              if (statusFilter) filtered = filtered.filter(s => s.status === statusFilter);
              if (priceFrom) filtered = filtered.filter(s => { const prices = (s.channel_access || []).filter(ca => ca.enabled).map(ca => ca.selling_price); return prices.length > 0 && Math.min(...prices) >= parseFloat(priceFrom); });
              if (priceTo) filtered = filtered.filter(s => { const prices = (s.channel_access || []).filter(ca => ca.enabled).map(ca => ca.selling_price); return prices.length > 0 && Math.min(...prices) <= parseFloat(priceTo); });
              if (channelFilter) filtered = filtered.filter(s => (s.channel_access || []).some(ca => ca.channel_id === channelFilter && ca.enabled));
              if (filtered.length === 0) return <tr><td colSpan={10} className="text-center py-8 text-zinc-400 text-xs">No SKUs match filters</td></tr>;
              return filtered.map(s => {
              const enabledCh = (s.channel_access || []).filter(ca => ca.enabled).length;
              return (
                <tr key={s.sku_id} data-testid={`sku-row-${s.sku_id}`}>
                  <td className="font-mono text-xs font-semibold text-zinc-900">{s.sku_id}</td>
                  <td style={{minWidth: 200, whiteSpace: 'normal'}} title={s.sku_name}><span className="text-zinc-900 text-xs font-medium">{s.sku_name}</span>{s.variant_name && <br/>}{s.variant_name && <span className="text-[10px] text-zinc-400">{s.variant_name}</span>}
                    {/* BATCH 5 PART A (A6b): no cost, or a cost above the MRP, means PnL cannot
                        value this SKU. base_cost only reaches users allowed to see COGS. */}
                    {canViewCogs && s.base_cost !== undefined && (() => {
                      const cost = Number(s.base_cost) || 0;
                      const mrp = Number(s.mrp) || 0;
                      if (cost <= 0) return <span className="ml-1 px-1.5 py-0.5 text-[8px] font-semibold bg-red-100 text-red-700 rounded-sm" data-testid={`cogs-missing-${s.sku_id}`}>COGS missing</span>;
                      if (mrp > 0 && cost > mrp) return <span className="ml-1 px-1.5 py-0.5 text-[8px] font-semibold bg-red-100 text-red-700 rounded-sm" data-testid={`cogs-above-mrp-${s.sku_id}`}>COGS above MRP</span>;
                      return null;
                    })()}</td>
                  <td className="text-xs text-zinc-500">{s.variant_name && !s.sku_name.includes(s.variant_name) ? s.variant_name : "-"}</td>
                  <td><span className="px-1.5 py-0.5 text-[10px] bg-zinc-100 text-zinc-600 border border-zinc-200 rounded-sm">{s.pack_format}</span></td>
                  <td className="num font-mono">{s.units_per_pack}</td>
                  <td className="num font-mono">{fmt(s.mrp)}</td>
                  <td className="num font-mono">{s.gst_percent}%</td>
                  <td className="num">{enabledCh > 0 ? <span className="px-1.5 py-0.5 text-[10px] bg-green-50 text-green-700 border border-green-200 rounded-sm">{enabledCh} active</span> : <span className="text-[10px] text-amber-600">None</span>}</td>
                  <td><span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm ${s.status === 'Draft' ? 'bg-zinc-200 text-zinc-600' : s.status === 'Pending Deletion' ? 'bg-amber-100 text-amber-700' : statusCls(s.status)}`}>{s.status}</span>{s.approval_status === "pending_new" && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-semibold bg-amber-100 text-amber-700 rounded-sm">Pending</span>}{s.approval_status === "rejected" && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-semibold bg-red-100 text-red-700 rounded-sm">Rejected</span>}{s.has_pending_changes && <span className="ml-1 px-1.5 py-0.5 text-[8px] font-semibold bg-blue-100 text-blue-700 rounded-sm">Changes Pending</span>}</td>
                  <td>
                    <div className="flex gap-1">
                      {canCreateSKU && <button onClick={() => openEditSKU(s)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`edit-sku-${s.sku_id}`}><Edit2 size={13} className="text-zinc-500" /></button>}
                      {canCreateSKU && s.status !== "Pending Deletion" && <button onClick={() => requestSKUDeletion(s)} className="p-1 hover:bg-red-50 rounded-sm" title="Request deletion"><Trash2 size={13} className="text-red-400" /></button>}
                      {canCreateSKU && s.approval_status === "rejected" && <button onClick={async () => { try { await api.post(`/catalogue/skus/${s.sku_id}/resubmit`); toast.success("Resubmitted"); loadData(); } catch(e) { toast.error(formatApiError(e.response?.data?.detail)); } }} className="p-1 hover:bg-amber-50 rounded-sm text-[10px] font-semibold text-amber-700">Resubmit</button>}
                    </div>
                  </td>
                </tr>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      {/* SKU Create/Edit Dialog */}
      <Dialog open={showSKUDialog} onOpenChange={setShowSKUDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sku-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingSKU ? `Edit SKU` : "New SKU"}</DialogTitle>
          </DialogHeader>
          {/* Draft restore banner */}
          {skuDraftBanner && !editingSKU && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between" data-testid="sku-draft-banner">
              <p className="text-xs text-blue-800">You have a saved draft for this SKU</p>
              <div className="flex gap-2">
                <Button className="rounded-sm h-6 text-[10px] px-2" onClick={() => {
                  try { const saved = JSON.parse(sessionStorage.getItem(skuDraftKey)); if (saved) setSkuForm(saved); } catch (err) { console.error("CataloguePage error:", err); }
                  setSkuDraftBanner(false);
                }}>Restore</Button>
                <Button variant="outline" className="rounded-sm h-6 text-[10px] px-2" onClick={() => { sessionStorage.removeItem(skuDraftKey); setSkuDraftBanner(false); }}>Discard</Button>
              </div>
            </div>
          )}
          <div className="space-y-5">
            {/* Identity */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border-b border-zinc-200 pb-1 mb-3">Identity</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">SKU Code / ID {editingSKU ? '' : '(editable)'}</Label>
                  <Input value={skuForm.sku_id || ""} onChange={(e) => setSkuForm({...skuForm, sku_id: e.target.value})}
                    placeholder="e.g. SKIPPI-ICEPOP-32ML or auto-generated" className="mt-1 rounded-sm font-mono" data-testid="sku-id-input" />
                  {editingSKU && skuForm.sku_id !== editingSKU.sku_id && skuForm.sku_id && (
                    <p className="text-[10px] text-amber-600 mt-1">Changing the SKU code will affect historical orders, inventory, and reports.</p>
                  )}
                </div>
                <div><Label className="text-xs">SKU Name</Label><Input value={skuForm.sku_name} onChange={(e) => setSkuForm({...skuForm, sku_name: e.target.value})} placeholder="Auto-generated if blank" className="mt-1 rounded-sm" data-testid="sku-name-input" /></div>
                <div><Label className="text-xs">Variant / Flavour</Label><Input value={skuForm.variant_name} onChange={(e) => setSkuForm({...skuForm, variant_name: e.target.value})} placeholder="e.g. Mango, Cola" className="mt-1 rounded-sm" data-testid="sku-variant-input" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={skuForm.status} onValueChange={(v) => setSkuForm({...skuForm, status: v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{SKU_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Barcode / EAN-13</Label><Input value={skuForm.barcode} onChange={(e) => setSkuForm({...skuForm, barcode: e.target.value})} className="mt-1 rounded-sm font-mono" /></div>
                <div>
                  <Label className="text-xs">Pack Format</Label>
                  <Select value={skuForm.pack_format} onValueChange={(v) => setSkuForm({...skuForm, pack_format: v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{PACK_FORMATS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Pack & Weight */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border-b border-zinc-200 pb-1 mb-3">Pack & Weight</p>
              <div className="grid grid-cols-4 gap-3">
                <div><Label className="text-xs">Units/Pack</Label><Input type="number" value={skuForm.units_per_pack} onChange={(e) => setSkuForm({...skuForm, units_per_pack: parseInt(e.target.value) || 1})} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs">Net Weight/Vol</Label><Input value={skuForm.net_weight_volume} onChange={(e) => setSkuForm({...skuForm, net_weight_volume: e.target.value})} placeholder="e.g. 60" className="mt-1 rounded-sm" /></div>
                <div>
                  <Label className="text-xs">Unit</Label>
                  <Select value={skuForm.weight_unit} onValueChange={(v) => setSkuForm({...skuForm, weight_unit: v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{WEIGHT_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Gross Weight</Label><Input value={skuForm.gross_weight} onChange={(e) => setSkuForm({...skuForm, gross_weight: e.target.value})} placeholder="g or kg" className="mt-1 rounded-sm" /></div>
              </div>
            </div>

            {/* Packaging */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border-b border-zinc-200 pb-1 mb-3">Packaging</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Primary Packaging</Label>
                  <Select value={skuForm.primary_packaging || "none"} onValueChange={(v) => setSkuForm({...skuForm, primary_packaging: v === "none" ? "" : v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select --</SelectItem>{PRIMARY_PACKAGING.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Secondary Packaging</Label>
                  <Select value={skuForm.secondary_packaging || "none"} onValueChange={(v) => setSkuForm({...skuForm, secondary_packaging: v === "none" ? "" : v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select --</SelectItem>{SECONDARY_PACKAGING.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Outer Packaging</Label>
                  <Select value={skuForm.outer_carton || "none"} onValueChange={(v) => setSkuForm({...skuForm, outer_carton: v === "none" ? "" : v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select --</SelectItem>{OUTER_PACKAGING.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div><Label className="text-xs">Units/Inner Box</Label><Input type="number" value={skuForm.units_per_inner_box} onChange={(e) => setSkuForm({...skuForm, units_per_inner_box: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs">Units/Master Carton</Label><Input type="number" value={skuForm.units_per_master_carton} onChange={(e) => setSkuForm({...skuForm, units_per_master_carton: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs">Carton Weight (kg)</Label><Input value={skuForm.carton_weight} onChange={(e) => setSkuForm({...skuForm, carton_weight: e.target.value})} className="mt-1 rounded-sm" /></div>
              </div>
            </div>

            {/* Pricing & Tax */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border-b border-zinc-200 pb-1 mb-3">Pricing & Tax</p>
              <div className="grid grid-cols-5 gap-3">
                <div><Label className="text-xs">MRP (INR)</Label><Input type="number" step="0.01" value={skuForm.mrp} onChange={(e) => setSkuForm({...skuForm, mrp: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="sku-mrp-input" /></div>
                {canViewCogs && <div><Label className="text-xs">COGS (INR) *</Label><Input type="number" step="0.01" value={skuForm.base_cost} onChange={(e) => setSkuForm({...skuForm, base_cost: parseFloat(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="sku-cogs-input" />
                  {/* BATCH 4 (4.1a/4.1e): mandatory, and never above the MRP */}
                  {!(parseFloat(skuForm.base_cost) > 0) && <p className="text-[10px] text-red-600 mt-0.5" data-testid="sku-cogs-missing">COGS missing — required before this SKU can be approved</p>}
                  {parseFloat(skuForm.base_cost) > 0 && parseFloat(skuForm.mrp) > 0 && parseFloat(skuForm.base_cost) > parseFloat(skuForm.mrp) && <p className="text-[10px] text-red-600 mt-0.5" data-testid="sku-cogs-above-mrp">COGS is higher than the MRP — check the value</p>}</div>}
                <div><Label className="text-xs">HSN Code</Label><Input value={skuForm.hsn_code} onChange={(e) => setSkuForm({...skuForm, hsn_code: e.target.value})} className="mt-1 rounded-sm" /></div>
                <div>
                  <Label className="text-xs">GST %</Label>
                  <Select value={String(skuForm.gst_percent)} onValueChange={(v) => setSkuForm({...skuForm, gst_percent: parseFloat(v)})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{GST_RATES.map(r => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">GST Treatment</Label>
                  <Select value={skuForm.gst_treatment} onValueChange={(v) => setSkuForm({...skuForm, gst_treatment: v})}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{GST_TREATMENTS.map(gt => <SelectItem key={gt} value={gt}>{gt}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Stock Control */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border-b border-zinc-200 pb-1 mb-3">Stock Control</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Reorder Level (units)</Label><Input type="number" value={skuForm.reorder_level} onChange={(e) => setSkuForm({...skuForm, reorder_level: parseInt(e.target.value) || 0})} className="mt-1 rounded-sm" data-testid="sku-reorder-input" /></div>
                <div><Label className="text-xs">Default MOQ (units)</Label><Input type="number" value={skuForm.moq} onChange={(e) => setSkuForm({...skuForm, moq: parseInt(e.target.value) || 1})} className="mt-1 rounded-sm" /></div>
              </div>
            </div>

            {/* Channel Access — the key section */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 border-b border-zinc-200 pb-1 mb-3">Channel Access</p>
              <p className="text-[10px] text-zinc-500 mb-3">Toggle ON to make this SKU available in a channel. Selling price is mandatory for enabled channels.</p>
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-[10px] font-semibold uppercase text-zinc-500 px-3 py-2 text-left">Channel</th>
                      <th className="text-[10px] font-semibold uppercase text-zinc-500 px-2 py-2 text-center w-16">Sell?</th>
                      <th className="text-[10px] font-semibold uppercase text-zinc-500 px-2 py-2 text-right w-28">Selling Price (INR)</th>
                      <th className="text-[10px] font-semibold uppercase text-zinc-500 px-2 py-2 text-right w-20">MOQ</th>
                      <th className="text-[10px] font-semibold uppercase text-zinc-500 px-2 py-2 text-right w-24">Max Disc %</th>
                      <th className="text-[10px] font-semibold uppercase text-zinc-500 px-2 py-2 text-center w-24">Carton mult.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skuForm.channel_access.map((ca, idx) => (
                      <tr key={ca.channel_id} className={`border-b border-zinc-100 ${ca.enabled ? "bg-white" : "bg-zinc-50 opacity-60"}`} data-testid={`ca-row-${idx}`}>
                        <td className="px-3 py-2 text-xs font-medium text-zinc-700">{ca.channel_name}</td>
                        <td className="px-2 py-2 text-center">
                          <Switch checked={ca.enabled} onCheckedChange={(v) => updateChannelAccess(idx, "enabled", v)} className="scale-75" data-testid={`ca-toggle-${idx}`} />
                        </td>
                        <td className="px-2 py-2">
                          <Input type="number" step="0.01" value={ca.selling_price || ""} onChange={(e) => updateChannelAccess(idx, "selling_price", parseFloat(e.target.value) || 0)}
                            disabled={!ca.enabled} className={`h-7 text-xs text-right rounded-sm font-mono w-full ${ca.enabled && !ca.selling_price ? 'border-red-400' : ''}`} data-testid={`ca-price-${idx}`} placeholder="0.00" />
                        </td>
                        <td className="px-2 py-2">
                          <Input type="number" value={ca.moq || ""} onChange={(e) => updateChannelAccess(idx, "moq", parseInt(e.target.value) || 0)}
                            disabled={!ca.enabled} className="h-7 text-xs text-right rounded-sm font-mono w-full" placeholder="0" />
                        </td>
                        <td className="px-2 py-2">
                          <Input type="number" step="0.1" value={ca.max_discount_percent || ""} onChange={(e) => updateChannelAccess(idx, "max_discount_percent", parseFloat(e.target.value) || 0)}
                            disabled={!ca.enabled} className="h-7 text-xs text-right rounded-sm font-mono w-full" data-testid={`ca-maxdisc-${idx}`} placeholder="0" />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <select value={ca.carton_multiple_override === true ? "true" : ca.carton_multiple_override === false ? "false" : "null"} onChange={(e) => { const v = e.target.value; updateChannelAccess(idx, "carton_multiple_override", v === "true" ? true : v === "false" ? false : null); }} disabled={!ca.enabled} className="h-7 text-[10px] rounded-sm border px-1" data-testid={`ca-carton-${idx}`}>
                            <option value="null">Inherit</option>
                            <option value="true">Force ON</option>
                            <option value="false">Force OFF</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowSKUDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            {!editingSKU && <Button variant="outline" onClick={() => saveSKU("Draft")} className="rounded-sm text-xs border-zinc-400" data-testid="save-sku-draft-btn">Save as Draft</Button>}
            {editingSKU && editingSKU.status === "Draft" && <Button onClick={() => saveSKU("Active")} className="rounded-sm text-xs bg-green-600 hover:bg-green-700" data-testid="make-live-btn">Make Live</Button>}
            <Button onClick={() => saveSKU()} className="rounded-sm text-xs" data-testid="save-sku-btn">{editingSKU ? "Save SKU" : "Save SKU"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
